import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ROTULO_TIPO_MARCACAO, type MarcacaoNaGrade } from '@arena/shared/schemas';
import { hojeNoFuso } from '@arena/shared/tempo';
import { agendaApi } from '../api/agenda.js';
import { mensagemDoErro } from '../api/cliente.js';
import { useSessao } from '../auth/SessaoContext.jsx';
import {
  corSolidaDaMarcacao,
  dataPorExtenso,
  horaCurta,
  horaNaArena,
  seloDoPagamento,
  subtituloDaMarcacao,
  tituloDaMarcacao,
} from '../agenda/formatos.js';
import { Erro, Esqueleto } from '../ui/estados.jsx';
import { Icone } from '../ui/Icone.js';
import { Card, Feed, ItemDeFeed, Nota, Vazio } from '../ui/primitivos.jsx';
// 12 · planos
import { CobrancasDoMes } from '../planos/CobrancasDoMes.jsx';
import { CardDeHorasACobrar } from '../agenda/HorasACobrar.jsx';

/**
 * `#v-dash` — por enquanto, só o que já existe.
 *
 * O painel executivo inteiro — receita, inadimplência, ocupação da
 * semana, o checklist — é o documento 17, e depende das métricas do 16.
 * O que já está aqui veio dos módulos que fecharam: "Próximas na agenda"
 * (06), "Horas a cobrar hoje" (06 + 11) e "Cobranças do mês" (12). O
 * resto chega quando os números tiverem uma fonte única de cálculo.
 *
 * "Horas a cobrar hoje" é o cartão que fecha o dia: as horas que a agenda
 * registrou, com valor e com quem é o responsável, separando o que já
 * entrou no caixa do que falta cobrar. Clicar numa linha abre a baixa, e
 * a baixa é o que alimenta o financeiro — é por aqui que a entrada de uma
 * locação nasce, sem ninguém digitar lançamento à mão.
 */
export function Painel() {
  const navegar = useNavigate();
  const { pode } = useSessao();
  const verAgenda = pode('agenda', 'ler');
  // 12 · planos
  const verCobrancas = pode('cobrancas', 'ler');

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Painel de hoje</h1>
          <p>O que acontece nas quadras hoje.</p>
        </div>
      </div>

      <Nota>
        <b>Painel executivo.</b> Receita recorrente, inadimplência, ocupação da semana e o checklist
        do dia chegam no documento 17, depois das métricas do 16 — para que o painel e o BI nunca
        mostrem dois números diferentes para a mesma coisa. Os cartões abaixo já são os de verdade.
      </Nota>

      {verAgenda && (
        <div className="grid g-2-1">
          <ProximasNaAgenda />
          <CardDeHorasACobrar
            titulo="Horas a cobrar hoje"
            dica="o que a agenda registrou"
            vazio="Nenhuma hora de quadra a cobrar hoje."
            limite={6}
            acao={
              <button type="button" className="link" onClick={() => navegar('/financeiro')}>
                Ver no financeiro
              </button>
            }
          />
        </div>
      )}
      {/* 12 · planos */}
      {verCobrancas && <CobrancasDoMes />}
      {!verAgenda && !verCobrancas && <Vazio>Nada para mostrar ao seu perfil ainda.</Vazio>}
    </>
  );
}

function ProximasNaAgenda() {
  const navegar = useNavigate();
  const consulta = useQuery({
    queryKey: ['agenda', 'proximas'],
    queryFn: () => agendaApi.proximas(6),
    // "em 40 min" envelhece: um minuto é o bastante para o cartão não mentir.
    refetchInterval: 60_000,
  });

  const acao = (
    <button type="button" className="link" onClick={() => navegar('/agenda')}>
      Ver dia completo
    </button>
  );

  if (consulta.isPending) {
    return (
      <Card titulo="Próximas na agenda" acao={acao}>
        <div style={{ display: 'grid', gap: 12 }} aria-busy="true">
          {[0, 1, 2].map((i) => (
            <Esqueleto key={i} altura={36} />
          ))}
        </div>
      </Card>
    );
  }

  if (consulta.isError) {
    return (
      <Card titulo="Próximas na agenda" acao={acao}>
        <Erro
          detalhe={mensagemDoErro(consulta.error)}
          onTentarDeNovo={() => void consulta.refetch()}
        />
      </Card>
    );
  }

  const { fuso, quadras, marcacoes } = consulta.data;
  const quadraPorId = new Map(quadras.map((q) => [q.id, q]));

  return (
    <Card titulo="Próximas na agenda" dica={dataPorExtenso(hojeNoFuso(fuso))} acao={acao}>
      {marcacoes.length === 0 ? (
        <Vazio>Nada mais na agenda hoje.</Vazio>
      ) : (
        <Feed>
          {marcacoes.map((m) => {
            const quadra = quadraPorId.get(m.quadraId);
            const selo = seloDoPagamento(m.formaPagamento, m.situacaoPagamento);
            return (
              <ItemDeFeed
                key={m.id}
                marca={
                  <span className="sq" style={{ background: corSolidaDaMarcacao(m, quadra) }}>
                    <Icone nome={m.tipo === 'Bloqueio' ? 'lock' : 'ball'} />
                  </span>
                }
                titulo={`${tituloDaMarcacao(m)} · ${horaCurta(horaNaArena(m.inicio, fuso))}`}
                subtitulo={[quadra?.nome, subtituloDaMarcacao(m)].filter(Boolean).join(' · ')}
                direita={
                  <>
                    <b>{quando(m, fuso)}</b>
                    <small>{selo ?? ROTULO_TIPO_MARCACAO[m.tipo]}</small>
                  </>
                }
                onClick={() => navegar(`/agenda?marcacao=${m.id}`)}
              />
            );
          })}
        </Feed>
      )}
    </Card>
  );
}

/** "agora", "em 40 min", ou a hora — como a coluna da direita do protótipo. */
function quando(marcacao: MarcacaoNaGrade, fuso: string): string {
  const agora = Date.now();
  const inicio = Date.parse(marcacao.inicio);
  if (inicio <= agora) return 'agora';
  const minutos = Math.round((inicio - agora) / 60_000);
  return minutos <= 60 ? `em ${minutos} min` : horaNaArena(marcacao.inicio, fuso);
}
